import { syntaxTree } from "@codemirror/language";
import type { EditorState } from "@codemirror/state";
import type { SyntaxNode } from "@lezer/common";
import type { Connector } from "@/types/api";
import { propertyKey, sectionOf } from "./pipelineCompletion";

// The ghost hints the Full pipeline editor draws inside empty "" reference
// values — an input's placeholder, per field: the template ships every
// service_name / resource empty (pipelineTemplate.ts), and a bare "" says
// nothing about what belongs there. Pure: finds the slots and their copy;
// emptySlotHintsView.ts turns them into decorations.

/** Placeholder labels by top-level section and key — /studio's template
 *  values (`your-source`, …), now labels only: the editor draws them as
 *  ghost hints and the graph strip shows them on cards whose value is
 *  still empty. Only these keys, in these sections, get one. */
const PLACEHOLDERS: Record<string, Record<string, string>> = {
  source: { service_name: "your-source", resource: "your-collection" },
  middlewares: {
    service_name: "your-udf-service",
    resource: "your-script.rhai",
  },
  sinks: { service_name: "your-sink", resource: "your-destination" },
  schemas: { service_name: "your-schema-service", resource: "your-schema" },
};

/** An entry as the labelling sees it: only the two reference keys matter. */
type RefEntry = { service_name?: string; resource?: string };

/** One entry with its empty "" references swapped for the section's labels.
 *  Anything that isn't an object of a known section passes through. */
function labelEmptyRefs<T extends RefEntry>(entry: T, section: string): T {
  const labels = PLACEHOLDERS[section];
  if (!isObject(entry) || !labels) return entry;
  return {
    ...entry,
    ...(entry.service_name === "" ? { service_name: labels.service_name } : {}),
    ...(entry.resource === "" ? { resource: labels.resource } : {}),
  };
}

/** The same, for a section that holds a list of entries. */
function labelledEntries<T extends RefEntry>(
  entries: T[],
  section: string,
): T[] {
  return entries.map((entry) => labelEmptyRefs(entry, section));
}

/** A copy of a parsed pipeline doc with every empty "" service_name /
 *  resource replaced by its placeholder label, for display only (the
 *  graph strip's cards) — the document itself keeps the empties. */
export function withPlaceholderRefs(
  doc: Partial<Connector>,
): Partial<Connector> {
  return {
    ...doc,
    ...(doc.source ? { source: labelEmptyRefs(doc.source, "source") } : {}),
    ...(Array.isArray(doc.middlewares)
      ? { middlewares: labelledEntries(doc.middlewares, "middlewares") }
      : {}),
    ...(Array.isArray(doc.sinks)
      ? { sinks: labelledEntries(doc.sinks, "sinks") }
      : {}),
    ...(Array.isArray(doc.schemas)
      ? { schemas: labelledEntries(doc.schemas, "schemas") }
      : {}),
  };
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

export interface EmptySlotHint {
  /** The position between the quotes of the empty string. */
  pos: number;
  hint: string;
}

/** A property's value node when — and only when — it is an empty "" string. */
function emptyStringValueOf(
  state: EditorState,
  property: SyntaxNode,
): SyntaxNode | null {
  const value = property.lastChild;
  if (!value || value.name !== "String") return null;
  const text = state.doc.sliceString(value.from, value.to);
  return text === '""' ? value : null;
}

/** The top-level section a property's own object belongs to, if any. */
function sectionOfProperty(
  state: EditorState,
  property: SyntaxNode,
): string | null {
  const object = property.parent;
  if (!object || object.name !== "Object") return null;
  return sectionOf(state, object)?.section ?? null;
}

/** The placeholder copy this property's key earns in its section — only
 *  the keys PLACEHOLDERS lists, in the sections it lists them under. */
function placeholderFor(
  state: EditorState,
  property: SyntaxNode,
): string | undefined {
  const section = sectionOfProperty(state, property);
  const key = propertyKey(state, property);
  if (!section || !key) return undefined;
  return PLACEHOLDERS[section]?.[key];
}

/** The hint one Property node carries, if its value is an empty slot. */
function hintForProperty(
  state: EditorState,
  property: SyntaxNode,
): EmptySlotHint | null {
  const value = emptyStringValueOf(state, property);
  if (!value) return null;
  const hint = placeholderFor(state, property);
  return hint ? { pos: value.from + 1, hint } : null;
}

/** Every empty "" service_name / resource value in a stage or schema entry,
 *  with its hint, in document order. Partial (unparsable) documents still
 *  yield hints for the entries the tree could resolve. */
export function emptySlotHintsIn(state: EditorState): EmptySlotHint[] {
  const hints: EmptySlotHint[] = [];
  const cursor = syntaxTree(state).cursor();
  do {
    if (cursor.name !== "Property") continue;
    const hint = hintForProperty(state, cursor.node);
    if (hint) hints.push(hint);
  } while (cursor.next());
  return hints;
}
